import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import techSheetService from '../services/techSheetService';
import ConfirmModal from './ConfirmModal';
import PalletDiagram from './PalletDiagram';

const MAX_IMG_BYTES = 1.5 * 1024 * 1024;
const ALLOWED_IMG_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const LB_TO_KG = 0.453592;
const KG_TO_LB = 2.20462;
const toKgWeight = (value, unit) => value == null ? null : (unit === 'kg' ? Number(value) : Number(value) * LB_TO_KG);
const fromKgWeight = (kg, unit) => unit === 'kg' ? kg : kg * KG_TO_LB;

async function compressImage(file, maxBytes = MAX_IMG_BYTES) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      // Cap dimensions at 1500px on the longest side
      const MAX_DIM = 1500;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);

      const tryQuality = (q) => {
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('Canvas export failed')); return; }
          if (blob.size <= maxBytes || q <= 0.3) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            tryQuality(Math.round((q - 0.1) * 10) / 10);
          }
        }, 'image/jpeg', q);
      };
      tryQuality(0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Could not read image')); };
    img.src = objectUrl;
  });
}

const ALLERGEN_LABELS = {
  gluten:        'Cereals containing gluten (wheat, barley, rye, oat, spelt, kamut) and derived products',
  crustaceans:   'Crustaceans and derived products',
  eggs:          'Eggs and derived products',
  fish:          'Fish and derived products',
  peanuts:       'Peanut and derived products',
  soy:           'Soy and derived products',
  nuts:          'Nuts (almonds, hazelnuts, walnuts, cashews, pecan, Brazil, pistachios, macadamia, pine nuts) and derived products',
  milk:          'Milk and derived products',
  mustard:       'Mustard and derived products',
  sesame:        'Sesame seed and derived products',
  sulphurDioxide:'Sulphur Dioxide and sulfites (>10mg/kg or mg/l expressed as SO₂)',
  lupin:         'Lupin bean and lupin products',
  mollusks:      'Mollusks and mollusk products',
};

const DEFAULT_PALLET = {
  // Box/unit dims — same as master case dims, entered once here
  unitDimL: '', unitDimW: '', unitDimH: '', dimUnit: 'in',
  // Stacking
  gridCols: 3, unitsPerLayer: 6, numberOfLayers: 6,
  // Optional secondary zone of boxes rotated 90°, filling leftover pallet-length space beyond
  // the main grid — { cols, rows, units } or null. Auto-suggested only, see autoArrange().
  mixedZone: null,
  // Pallet slab
  palletL: 48, palletW: 40, palletH: 6,
  // Weight
  unitWeightValue: '', weightUnit: 'lbs',
  // Computed on save (read by preview page)
  totalWeight: '', surfaceUsage: '', overhangW: 0, overhangL: 0,
};

// unitsPerLayer is always the COMBINED total (main grid + mixedZone, if any) — every other
// consumer of this config (weight calc, spec table, PDF export) just wants "how many units in
// a layer" and shouldn't need to know about mixed zones. Only the geometry here and in
// PalletDiagram.js needs to split it back out via mixedZone.units.
function computePalletDerived(p) {
  const cols = Number(p.gridCols) || 1;
  const totalUnits = Number(p.unitsPerLayer) || 0;
  const mixedZone = p.mixedZone && Number(p.mixedZone.units) > 0 ? p.mixedZone : null;
  const mainUnits = mixedZone ? Math.max(totalUnits - mixedZone.units, 0) : totalUnits;
  const rows = Math.ceil(mainUnits / cols);
  const lastRowCount = mainUnits % cols === 0 ? cols : mainUnits % cols;
  const unitDimL = Number(p.unitDimL) || 0;
  const unitDimW = Number(p.unitDimW) || 0;
  const mainLoadL = cols * unitDimL;
  const mainLoadW = rows * unitDimW;
  // Mixed-zone boxes are rotated 90° (L/W swap) and sit in the leftover strip beyond the
  // main grid's footprint, along the pallet-length axis — see autoArrange().
  const mixedLoadL = mixedZone ? mixedZone.cols * unitDimW : 0;
  const mixedLoadW = mixedZone ? mixedZone.rows * unitDimL : 0;
  const loadL = mainLoadL + mixedLoadL;
  const loadW = Math.max(mainLoadW, mixedLoadW);
  const palletL = Number(p.palletL) || 0;
  const palletW = Number(p.palletW) || 0;
  const surfaceUsage = palletL && palletW && loadL && loadW
    ? ((loadL * loadW) / (palletL * palletW) * 100).toFixed(1)
    : '';
  const overhangL = loadL > palletL ? ((loadL - palletL) / 2).toFixed(1) : 0;
  const overhangW = loadW > palletW ? ((loadW - palletW) / 2).toFixed(1) : 0;
  const layers = Number(p.numberOfLayers) || 0;
  const perUnit = Number(p.unitWeightValue) || 0;
  const totalWeight = perUnit && totalUnits
    ? (totalUnits * layers * perUnit).toFixed(1)
    : '';
  const totalCases = totalUnits && layers ? totalUnits * layers : 0;
  const nonUniform = lastRowCount !== cols && mainUnits > 0;
  return { loadL, loadW, rows, lastRowCount, nonUniform, surfaceUsage, overhangL, overhangW, totalWeight, totalCases, mixedZone };
}

function autoArrange(p) {
  const uL = Number(p.unitDimL);
  const uW = Number(p.unitDimW);
  const pL = Number(p.palletL);
  const pW = Number(p.palletW);
  if (!uL || !uW || !pL || !pW) return null;
  const suggestedCols = Math.floor(pL / uL);
  const suggestedRows = Math.floor(pW / uW);
  if (suggestedCols < 1 || suggestedRows < 1) return null;
  const mainUnits = suggestedCols * suggestedRows;

  // Leftover strip beyond the main grid, along the pallet-length axis — if a box rotated 90°
  // (its width now running along that axis) fits, suggest filling that strip too instead of
  // leaving it empty. Packaging-design tools call this a "mixed"/interlocked layer pattern.
  const remainingL = pL - suggestedCols * uL;
  let mixedZone = null;
  if (remainingL >= uW) {
    const mixedCols = Math.floor(remainingL / uW);
    const mixedRows = Math.floor(pW / uL);
    const mixedUnits = mixedCols * mixedRows;
    if (mixedCols > 0 && mixedRows > 0 && mixedUnits > 0) {
      mixedZone = { cols: mixedCols, rows: mixedRows, units: mixedUnits };
    }
  }

  const units = mainUnits + (mixedZone?.units || 0);
  return { cols: suggestedCols, rows: suggestedRows, units, mainUnits, mixedZone };
}

function statusBadge(status) {
  const map = { draft: 'warning', approved: 'success', archived: 'secondary' };
  return <span className={`badge text-bg-${map[status] || 'light'} text-capitalize`}>{status}</span>;
}

function newPresentationBlock() {
  return {
    _key: `new-${Math.random().toString(36).slice(2)}`,
    id: null,
    presentationId: '',
    caseUpc: '',
    unitUpc: '',
    label: '',
    unitSale: '',
    unitsPerMasterCase: '',
    packagingType: '',
    retailPackagingImageUrl: '',
    pallet: { ...DEFAULT_PALLET },
  };
}

function blockFromServer(b) {
  const raw = b.palletConfig;
  const pallet = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
  return {
    _key: `db-${b.id}`,
    id: b.id,
    presentationId: b.presentationId || '',
    caseUpc: b.caseUpc || '',
    unitUpc: b.unitUpc || '',
    label: b.label || b.Presentation?.name || '',
    unitSale: b.unitSale || '',
    unitsPerMasterCase: b.unitsPerMasterCase ?? '',
    packagingType: b.packagingType || '',
    retailPackagingImageUrl: b.retailPackagingImageUrl || '',
    pallet: { ...DEFAULT_PALLET, ...pallet },
  };
}

export default function TechSheetPanel({ productId, productName, onDirtyChange }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [presentations, setPresentations] = useState([]);
  const [deletedPresentationIds, setDeletedPresentationIds] = useState([]);
  const [confirm, setConfirm] = useState(null);

  // Unsaved-changes guard — lets the parent (AdminProductPage's tab switcher) warn
  // before navigating away and silently discarding an in-progress edit.
  const [isDirty, setIsDirty] = useState(false);
  const skipDirtyRef = useRef(true); // true right after (re)load, to ignore that load's own setForm/setPresentations calls

  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!editingId) return;
    if (skipDirtyRef.current) { skipDirtyRef.current = false; return; }
    setIsDirty(true);
  }, [form, presentations, deletedPresentationIds, editingId]);

  const [computedData, setComputedData] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [productTypePrompt, setProductTypePrompt] = useState('');

  const [cloneModal, setCloneModal] = useState(false);
  const [allCompanySheets, setAllCompanySheets] = useState([]);
  const [cloneSourceId, setCloneSourceId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await techSheetService.listForProduct(productId);
      setSheets(data);
    } catch { setError('Failed to load tech sheets.'); }
    finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  // Auto-dismiss success/error banners so they don't linger after navigating back
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    techSheetService.getComputedData(productId)
      .then(setComputedData)
      .catch(() => {});
  }, [productId]);

  // Backfill auto-populated fields when computedData arrives after edit was already open
  useEffect(() => {
    if (!computedData || !editingId) return;
    setForm(p => ({
      ...p,
      sheetNumber: p.sheetNumber || computedData.nextSheetNumber || '',
      netWeight: p.netWeight || computedData.netWeight || '',
      lotNumberFormat: p.lotNumberFormat || computedData.lotCodeFormat || '',
      lotNumberKey: p.lotNumberKey || computedData.lotCodeKey || '',
      ingredientListOverride: p.ingredientListOverride || computedData.ingredientList || '',
    }));
  }, [computedData, editingId]);

  // Backfill caseUpc/unitUpc for blocks that were already linked to a product
  // presentation before computedData finished loading (or were linked in an earlier
  // session, before these fields existed) — linkPresentation() only fires on an actual
  // dropdown change, so an already-linked block would otherwise never pick these up.
  useEffect(() => {
    if (!computedData?.productPresentations?.length) return;
    setPresentations(prev => prev.map(b => {
      if (!b.presentationId) return b;
      const linked = computedData.productPresentations.find(p => String(p.id) === String(b.presentationId));
      if (!linked) return b;
      const caseUpc = b.caseUpc || linked.gtin || b.caseUpc;
      const unitUpc = b.unitUpc || linked.unitGtin || b.unitUpc;
      return (caseUpc !== b.caseUpc || unitUpc !== b.unitUpc) ? { ...b, caseUpc, unitUpc } : b;
    }));
  }, [computedData]);

  const draft = sheets.find(s => s.status === 'draft');
  const approved = sheets.find(s => s.status === 'approved');

  const startEdit = async (sheetOrId) => {
    const sheetId = typeof sheetOrId === 'object' ? sheetOrId.id : sheetOrId;
    setEditingId(sheetId);
    setError(''); setSuccess('');
    skipDirtyRef.current = true;
    setIsDirty(false);
    try {
      const sheet = await techSheetService.get(sheetId);
      setForm({
        sheetNumber: sheet.sheetNumber || computedData?.nextSheetNumber || '',
        description: sheet.description || '',
        organolepticCharacteristics: sheet.organolepticCharacteristics || '',
        servingInstructions: sheet.servingInstructions || '',
        storageTemp: sheet.storageTemp || '',
        shelfLife: sheet.shelfLife || '',
        netWeight: sheet.netWeight || computedData?.netWeight || '',
        diameter: sheet.diameter || '',
        countryOfOrigin: sheet.countryOfOrigin || '',
        countryOfProcedence: sheet.countryOfProcedence || '',
        lotNumberFormat: sheet.lotNumberFormat || computedData?.lotCodeFormat || '',
        lotNumberKey: sheet.lotNumberKey || computedData?.lotCodeKey || '',
        isCertifiedNonGMO: sheet.isCertifiedNonGMO || false,
        isCertifiedOrganic: sheet.isCertifiedOrganic || false,
        notes: sheet.notes || '',
        productImageUrl: sheet.productImageUrl || '',
        ingredientListOverride: sheet.ingredientListOverride || computedData?.ingredientList || '',
      });
      setPresentations((sheet.PresentationBlocks || []).map(blockFromServer));
      setDeletedPresentationIds([]);
      setProductTypePrompt('');
    } catch (err) {
      setError('Failed to load sheet data.');
      setEditingId(null);
    }
  };

  const closeEditor = () => { setEditingId(null); setError(''); setSuccess(''); setIsDirty(false); };

  const cancelEdit = () => {
    if (!isDirty) { closeEditor(); return; }
    setConfirm({
      title: 'Unsaved Changes',
      message: 'You have unsaved changes in this draft. If you close now without saving, your work will be lost.',
      confirmLabel: 'Discard changes',
      cancelLabel: 'Stay and save',
      variant: 'warning',
      onConfirm: () => { closeEditor(); setConfirm(null); },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await techSheetService.update(editingId, form);

      const ops = [];
      for (const block of presentations) {
        const derived = computePalletDerived(block.pallet);
        const u = block.pallet.dimUnit || 'in';
        const masterCaseDimensions = (block.pallet.unitDimL || block.pallet.unitDimW || block.pallet.unitDimH)
          ? `${block.pallet.unitDimL || '?'}${u} × ${block.pallet.unitDimW || '?'}${u} × ${block.pallet.unitDimH || '?'}${u}`
          : '';
        const palletToSave = {
          ...block.pallet,
          surfaceUsage: derived.surfaceUsage,
          overhangL: derived.overhangL,
          overhangW: derived.overhangW,
          totalWeight: derived.totalWeight,
        };
        const payload = {
          presentationId: block.presentationId || null,
          caseUpc: block.caseUpc || null,
          unitUpc: block.unitUpc || null,
          label: block.label || null,
          unitSale: block.unitSale || null,
          unitsPerMasterCase: block.unitsPerMasterCase || null,
          packagingType: block.packagingType || null,
          masterCaseDimensions,
          palletConfig: palletToSave,
          retailPackagingImageUrl: block.retailPackagingImageUrl || null,
        };
        ops.push(
          block.id
            ? techSheetService.updatePresentation(editingId, block.id, payload)
            : techSheetService.createPresentation(editingId, payload)
        );
      }
      for (const delId of deletedPresentationIds) {
        ops.push(techSheetService.deletePresentation(editingId, delId));
      }
      await Promise.all(ops);

      // Stay in the editor — refresh form/presentations (real IDs for new blocks) in place
      await startEdit(editingId);
      setSuccess('Saved.');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  };

  const createDraft = async () => {
    setSaving(true);
    try {
      const sheet = await techSheetService.create(productId);
      await load();
      await startEdit(sheet.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create draft.');
    } finally { setSaving(false); }
  };

  const openCloneModal = async () => {
    setCloneSourceId('');
    setError('');
    try {
      const all = await techSheetService.list();
      // Only offer approved sheets from OTHER products (same company)
      const options = all.filter(s => s.status === 'approved' && s.productId !== productId);
      setAllCompanySheets(options);
      setCloneModal(true);
    } catch {
      setError('Could not load tech sheets to clone from.');
    }
  };

  const executeClone = async () => {
    if (!cloneSourceId) return;
    setSaving(true);
    try {
      const sheet = await techSheetService.cloneFrom(productId, cloneSourceId);
      await load();
      setCloneModal(false);
      await startEdit(sheet.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Clone failed.');
    } finally { setSaving(false); }
  };

  const approveDraft = (sheet) => {
    setConfirm({
      title: 'Approve Tech Sheet',
      message: `Approve Version ${sheet.version}? The current approved version (if any) will be archived. Once approved, create a new draft to make further changes.`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await techSheetService.approve(sheet.id);
          setSuccess('Tech sheet approved.');
          await load();
        } catch (err) {
          setError(err.response?.data?.error || 'Approval failed.');
        } finally { setSaving(false); setConfirm(null); }
      },
    });
  };

  const deleteDraft = (sheet) => {
    setConfirm({
      title: 'Delete Draft',
      message: `Delete the draft (Version ${sheet.version})? This cannot be undone.`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await techSheetService.delete(sheet.id);
          setEditingId(null);
          setIsDirty(false);
          await load();
        } catch (err) {
          setError(err.response?.data?.error || 'Delete failed.');
        } finally { setSaving(false); setConfirm(null); }
      },
    });
  };

  const generateWithAI = async () => {
    if (!productName) return;
    setAiGenerating(true);
    setError('');
    try {
      const result = await techSheetService.generateDescription({
        productName,
        ingredientList: form.ingredientListOverride || computedData?.ingredientList || '',
        productTypePrompt,
      });
      setForm(p => ({
        ...p,
        description: result.description || p.description,
        organolepticCharacteristics: result.organolepticCharacteristics || p.organolepticCharacteristics,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'AI generation failed. Please try again.');
    } finally { setAiGenerating(false); }
  };

  const f = (key) => ({
    value: form[key] ?? '',
    onChange: e => setForm(p => ({ ...p, [key]: e.target.value })),
  });

  const allergens = computedData?.allergens || {};

  const addPresentation = () => {
    setPresentations(prev => [...prev, newPresentationBlock()]);
  };

  const removePresentation = (key) => {
    const block = presentations.find(b => b._key === key);
    if (block?.id) setDeletedPresentationIds(ids => [...ids, block.id]);
    setPresentations(prev => prev.filter(b => b._key !== key));
  };

  const updatePresentationField = (key, field, value) => {
    setPresentations(prev => prev.map(b => b._key === key ? { ...b, [field]: value } : b));
  };

  const updatePresentationPalletField = (key, field, value) => {
    setPresentations(prev => prev.map(b => b._key === key ? { ...b, pallet: { ...b.pallet, [field]: value } } : b));
  };

  const linkPresentation = (key, presentationId) => {
    const linked = computedData?.productPresentations?.find(p => String(p.id) === String(presentationId));
    setPresentations(prev => prev.map(b => b._key === key
      ? {
          ...b,
          presentationId,
          label: linked ? linked.name : b.label,
          // Auto-fill from the linked presentation's case/unit GTIN — but never clobber a
          // value the user already typed in (e.g. re-linking after a manual override).
          caseUpc: b.caseUpc || linked?.gtin || '',
          unitUpc: b.unitUpc || linked?.unitGtin || '',
        }
      : b
    ));
  };

  if (loading) return <div className="text-center py-4 text-muted">Loading…</div>;

  return (
    <div>
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      {/* Version list */}
      {!editingId && (
        <div>
          {sheets.length === 0 && (
            <div className="text-center py-4 text-muted">
              <p className="mb-2">No tech sheets yet for this product.</p>
              <div className="d-flex justify-content-center gap-2">
                <button className="btn btn-primary btn-sm" onClick={createDraft} disabled={saving}>
                  + New from scratch
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={openCloneModal} disabled={saving}>
                  Clone from existing…
                </button>
              </div>
            </div>
          )}

          {sheets.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold" style={{ fontSize: 13 }}>Versions</span>
                {!draft && (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={createDraft}
                      disabled={saving}
                      title="Starts a new draft pre-filled with everything from the latest version — edit and approve to publish a revision"
                    >
                      + New Revision
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={openCloneModal} disabled={saving}>
                      Clone…
                    </button>
                  </div>
                )}
              </div>
              <table className="table table-sm table-hover align-middle mb-3" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>Sheet #</th>
                    <th>Approved</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map(s => (
                    <tr key={s.id}>
                      <td>v{s.version}</td>
                      <td>{statusBadge(s.status)}</td>
                      <td className="text-muted">{s.sheetNumber || '—'}</td>
                      <td className="text-muted">
                        {s.approvedAt ? new Date(s.approvedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {s.status === 'draft' && (
                          <>
                            <button className="btn btn-outline-primary btn-sm me-1" onClick={() => startEdit(s)}>Edit</button>
                            <button className="btn btn-success btn-sm me-1" onClick={() => approveDraft(s)} disabled={saving}>Approve</button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => deleteDraft(s)} disabled={saving}>Delete</button>
                          </>
                        )}
                        {s.status !== 'draft' && (
                          <a href={`/techsheets/${s.id}/preview`} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm">
                            Preview / PDF
                          </a>
                        )}
                        {s.status === 'draft' && (
                          <a href={`/techsheets/${s.id}/preview`} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm ms-1">
                            Preview
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {approved && (
                <div className="alert alert-success py-2" style={{ fontSize: 12 }}>
                  Active version: <strong>v{approved.version}</strong> — approved {new Date(approved.approvedAt).toLocaleDateString()}
                  {approved.ApprovedBy && (approved.ApprovedBy.firstName || approved.ApprovedBy.lastName) && ` by ${approved.ApprovedBy.firstName || ''} ${approved.ApprovedBy.lastName || ''}`.trimEnd()}.
                  {' '}<a href={`/techsheets/${approved.id}/preview`} target="_blank" rel="noopener noreferrer">Open PDF Preview</a>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Draft editor */}
      {editingId && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-semibold" style={{ fontSize: 13 }}>Editing Draft — {productName}</span>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
            </div>
          </div>

          <div style={{ maxHeight: 560, overflowY: 'auto', paddingRight: 4 }}>

            {/* ── Identity ── */}
            <Section title="Identity">
              <Row label="Sheet Number" hint="auto-generated — unique per company, you can override">
                <input type="text" className="form-control form-control-sm" placeholder="e.g. 1001" {...f('sheetNumber')} />
              </Row>
            </Section>

            {/* ── Ingredients (editable statement, pre-filled from the live recipe) ── */}
            <Section title="Ingredients">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="badge text-bg-info" style={{ fontSize: 10 }}>Pre-filled from product recipe — editable</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: 10, padding: '1px 6px' }}
                  disabled={!computedData?.ingredientList}
                  onClick={() => setForm(p => ({ ...p, ingredientListOverride: computedData?.ingredientList || '' }))}
                  title="Discard edits and re-pull the current list from the recipe"
                >
                  Reset to computed
                </button>
              </div>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                style={{ fontSize: 12 }}
                {...f('ingredientListOverride')}
              />
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                Fix ordering (must stay descending by weight) or rename ingredients for display here.
                This only changes how the list reads — <strong>allergens below are always detected
                live from the recipe</strong> and are not affected by edits here.
              </div>
            </Section>

            {/* ── AI Generator ── */}
            <Section title="AI Description Generator">
              <div className="mb-2" style={{ fontSize: 12, color: '#666' }}>
                Describe the product type below and click Generate to auto-fill the Description and Organoleptic fields.
              </div>
              <Row label="Product type / prompt">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g. frozen 12-inch margherita pizza with tomato sauce and mozzarella"
                  value={productTypePrompt}
                  onChange={e => setProductTypePrompt(e.target.value)}
                />
              </Row>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={generateWithAI}
                disabled={aiGenerating || !productTypePrompt.trim()}
              >
                {aiGenerating ? '✦ Generating…' : '✦ Generate with AI'}
              </button>
            </Section>

            {/* ── Product Info ── */}
            <Section title="Product Info">
              <Row label="Description">
                <textarea className="form-control form-control-sm" rows={2} {...f('description')} />
              </Row>
              <Row label="Organoleptic Characteristics">
                <textarea className="form-control form-control-sm" rows={3} {...f('organolepticCharacteristics')} />
              </Row>
              <Row label="Serving Instructions">
                <textarea className="form-control form-control-sm" rows={3} {...f('servingInstructions')} />
              </Row>

              {computedData?.servingSize && (
                <Row label="Serving Size">
                  <div className="form-control form-control-sm bg-light text-muted" style={{ cursor: 'default' }}>
                    {computedData.servingSize}
                    <span className="ms-2 badge text-bg-secondary" style={{ fontSize: 10 }}>from nutrition profile</span>
                  </div>
                </Row>
              )}

              <div className="row g-2">
                <div className="col-6">
                  <Row label="Storage Temp">
                    <input type="text" className="form-control form-control-sm" placeholder="-18°C / 0°F" {...f('storageTemp')} />
                  </Row>
                </div>
                <div className="col-6">
                  <Row label="Shelf Life">
                    <input type="text" className="form-control form-control-sm" placeholder="12 Months" {...f('shelfLife')} />
                  </Row>
                </div>
                <div className="col-6">
                  <Row label="Net Weight" hint="pre-filled from product">
                    <input type="text" className="form-control form-control-sm" placeholder="440g / 15.5oz" {...f('netWeight')} />
                  </Row>
                </div>
                <div className="col-6">
                  <Row label="Diameter / Size">
                    <input type="text" className="form-control form-control-sm" placeholder="30cm / 12 inch" {...f('diameter')} />
                  </Row>
                </div>
                <div className="col-6">
                  <Row label="Country of Origin">
                    <input type="text" className="form-control form-control-sm" placeholder="Italy" {...f('countryOfOrigin')} />
                  </Row>
                </div>
                <div className="col-6">
                  <Row label="Country of Procedence" hint="only if different from origin">
                    <input type="text" className="form-control form-control-sm" placeholder="Canada" {...f('countryOfProcedence')} />
                  </Row>
                </div>
              </div>
            </Section>

            {/* ── Compliance ── */}
            <Section title="Compliance">
              <div className="mb-2" style={{ fontSize: 12, color: '#666' }}>Certifications</div>
              <div className="form-check mb-2">
                <input
                  type="checkbox" className="form-check-input" id="ts-nongmo"
                  checked={form.isCertifiedNonGMO || false}
                  onChange={e => setForm(p => ({ ...p, isCertifiedNonGMO: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="ts-nongmo" style={{ fontSize: 13 }}>
                  Is the product certified <strong>Non-GMO</strong>?
                </label>
              </div>
              <div className="form-check mb-3">
                <input
                  type="checkbox" className="form-check-input" id="ts-organic"
                  checked={form.isCertifiedOrganic || false}
                  onChange={e => setForm(p => ({ ...p, isCertifiedOrganic: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="ts-organic" style={{ fontSize: 13 }}>
                  Is the product certified <strong>Organic</strong>?
                </label>
              </div>

              <div className="p-2 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="fw-semibold" style={{ fontSize: 12 }}>Allergens (EU — 13 major)</span>
                  <span className="badge text-bg-warning" style={{ fontSize: 10 }}>Read-only — sourced from ingredient database</span>
                </div>
                {computedData
                  ? Object.entries(ALLERGEN_LABELS).map(([key, label]) => (
                    <div key={key} className="d-flex align-items-start gap-2 mb-1">
                      <span
                        className={`badge ${allergens[key] ? 'text-bg-danger' : 'text-bg-light border'}`}
                        style={{ fontSize: 11, minWidth: 32, textAlign: 'center', marginTop: 1 }}
                      >
                        {allergens[key] ? 'YES' : 'NO'}
                      </span>
                      <span style={{ fontSize: 12 }}>{label}</span>
                    </div>
                  ))
                  : <span className="text-muted" style={{ fontSize: 12 }}>Loading allergen data…</span>
                }
                <div className="mt-2" style={{ fontSize: 11, color: '#888' }}>
                  To update allergens, edit the ingredient records in the Ingredients section.
                </div>
              </div>
            </Section>

            {/* ── Packaging & Logistics (shared across presentations) ── */}
            <Section title="Packaging & Logistics">
              <Row label="Lot Number Format" hint="pre-filled from company settings">
                <input type="text" className="form-control form-control-sm" placeholder="e.g. {MON}-1{YY}{DD}1" {...f('lotNumberFormat')} />
              </Row>
              <Row label="Lot Number Key" hint="legend for the format tokens">
                <input type="text" className="form-control form-control-sm" placeholder="e.g. MON: month letter, YY: 2-digit year, DD: day" {...f('lotNumberKey')} />
              </Row>
            </Section>

            {/* ── Presentations (per retail format: unit sale, master case, pallet, photo) ── */}
            <Section title="Presentations">
              <div className="mb-2" style={{ fontSize: 12, color: '#666' }}>
                Add one block per retail format this product ships in (e.g. retail single, retail double, food service).
                Shared info above (description, allergens, nutrition, lot code) applies to all of them.
              </div>
              {presentations.length === 0 && (
                <div className="text-center py-3 text-muted" style={{ fontSize: 12, border: '1px dashed #dee2e6', borderRadius: 6 }}>
                  No presentations yet.
                </div>
              )}
              {presentations.map((block, idx) => (
                <PresentationBlockCard
                  key={block._key}
                  index={idx}
                  block={block}
                  productPresentations={computedData?.productPresentations || []}
                  product={computedData?.product || null}
                  companyPalletWeightKg={computedData?.companyPalletWeightKg ?? null}
                  onField={(field, value) => updatePresentationField(block._key, field, value)}
                  onPalletField={(field, value) => updatePresentationPalletField(block._key, field, value)}
                  onLink={(presentationId) => linkPresentation(block._key, presentationId)}
                  onRemove={() => removePresentation(block._key)}
                />
              ))}
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={addPresentation}>
                + Add Presentation
              </button>
            </Section>

            {/* ── Images ── */}
            <Section title="Product Photos">
              <ImageUploadRow
                label="Product Photo"
                value={form.productImageUrl}
                onChange={url => setForm(p => ({ ...p, productImageUrl: url }))}
              />
              <div style={{ fontSize: 11, color: '#999' }}>
                Retail packaging photos are set per presentation, in the Presentations section above.
              </div>
            </Section>

            {/* ── Internal notes ── */}
            <Section title="Internal Notes (not printed)">
              <textarea className="form-control form-control-sm" rows={2} {...f('notes')} />
            </Section>
          </div>
        </div>
      )}

      {/* Clone modal */}
      {cloneModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1070 }} onClick={() => setCloneModal(false)} />
          <div className="modal fade show d-block" style={{ zIndex: 1080 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title" style={{ fontSize: 15 }}>Clone from existing tech sheet</h5>
                  <button className="btn-close" onClick={() => setCloneModal(false)} />
                </div>
                <div className="modal-body" style={{ fontSize: 13 }}>
                  <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                    Copies storage conditions, master case, lot code, pallet config, packaging type, certifications, and serving instructions from the selected sheet.
                    Net weight and sheet number will be auto-populated from <strong>{productName}</strong>.
                    Description and organoleptic characteristics will be left blank for AI generation.
                  </p>
                  {allCompanySheets.length === 0 ? (
                    <div className="text-muted text-center py-3">No approved tech sheets found for other products.</div>
                  ) : (
                    <div>
                      <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Select source tech sheet</label>
                      <select
                        className="form-select form-select-sm"
                        value={cloneSourceId}
                        onChange={e => setCloneSourceId(e.target.value)}
                      >
                        <option value="">— choose a sheet —</option>
                        {allCompanySheets.map(s => (
                          <option key={s.id} value={s.id}>
                            #{s.sheetNumber || s.id} — {s.Product?.name || `Product #${s.productId}`} (v{s.version})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setCloneModal(false)}>Cancel</button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={executeClone}
                    disabled={!cloneSourceId || saving}
                  >
                    {saving ? 'Cloning…' : 'Clone & Edit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {confirm && (
        <ConfirmModal
          show
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          cancelLabel={confirm.cancelLabel}
          variant={confirm.variant}
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel || (() => setConfirm(null))}
        />
      )}
    </div>
  );
}

function ImageUploadRow({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [compressed, setCompressed] = useState(false);
  const inputRef = useRef();

  const handleFile = async (file) => {
    setError('');
    setCompressed(false);

    if (!ALLOWED_IMG_TYPES.includes(file.type)) {
      setError('Only JPEG and PNG images are allowed.');
      return;
    }

    let fileToUpload = file;
    if (file.size > MAX_IMG_BYTES) {
      setUploading(true);
      try {
        fileToUpload = await compressImage(file);
        setCompressed(true);
      } catch {
        setError('Could not compress image. Please use a smaller file (max 1.5 MB).');
        setUploading(false);
        return;
      }
    }

    setUploading(true);
    try {
      const { uploadUrl, publicUrl } = await techSheetService.getImageUploadUrl(
        fileToUpload.name, fileToUpload.type, fileToUpload.size
      );
      await axios.put(uploadUrl, fileToUpload, { headers: { 'Content-Type': fileToUpload.type } });
      onChange(publicUrl);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>{label}</label>
      <div className="d-flex gap-2 align-items-start">
        {value && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img src={value} alt={label} style={{ height: 64, width: 64, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }} />
            <button
              type="button"
              onClick={() => { onChange(''); setCompressed(false); setError(''); }}
              style={{ position: 'absolute', top: -6, right: -6, background: '#dc3545', color: 'white', border: 'none', borderRadius: '50%', width: 18, height: 18, fontSize: 10, lineHeight: '18px', textAlign: 'center', cursor: 'pointer', padding: 0 }}
            >✕</button>
          </div>
        )}
        <div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : value ? 'Replace photo' : 'Upload photo'}
          </button>
          <div style={{ fontSize: 11, color: '#999', marginTop: 3 }}>
            JPEG or PNG — oversized images are automatically compressed
          </div>
          {compressed && !error && (
            <div style={{ fontSize: 11, color: '#0a6640', marginTop: 2 }}>
              ✓ Image was automatically compressed to fit under 1.5 MB
            </div>
          )}
          {error && (
            <div style={{ fontSize: 11, color: '#dc3545', marginTop: 3 }}>{error}</div>
          )}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
          />
        </div>
      </div>
    </div>
  );
}

function PresentationBlockCard({ index, block, productPresentations, product, companyPalletWeightKg, onField, onPalletField, onLink, onRemove }) {
  const pallet = block.pallet;
  const derived = computePalletDerived(pallet);
  const suggestion = autoArrange(pallet);
  const dimUnit = pallet.dimUnit || 'in';
  const weightUnit = pallet.weightUnit || 'lbs';

  const pf = (key) => ({
    value: pallet[key] ?? '',
    onChange: e => onPalletField(key, e.target.value),
  });

  // ── Case weight suggestion — net unit weight + wrapper weight, × units per pack,
  // × packs per case. E.g. a "double pack" (2 units/pack) presentation with 10 packs
  // per master case holds 20 base units, not 10 — units-per-pack comes from the
  // linked ProductPresentation's packaging details, not from the tech sheet block.
  const linkedPresentation = productPresentations.find(p => String(p.id) === String(block.presentationId));
  const unitsPerPack = Number(linkedPresentation?.unitsPerRetailBox) || 1;
  const packsPerCase = Number(block.unitsPerMasterCase) || 0;
  const unitsPerCase = packsPerCase * unitsPerPack;
  const unitNetKg = product?.unitNetWeightValue != null ? toKgWeight(product.unitNetWeightValue, product.unitNetWeightUnit) : null;
  const wrapperKg = product?.wrapperWeightKg || 0;
  // Same tare chain the BOL module uses (net + wrapper + retail box + master case):
  // each retail box/pack has its own material weight, and there are packsPerCase of
  // them per case, plus the outer shipping case's own material weight once per case.
  const retailBoxKg = Number(linkedPresentation?.retailBoxWeightKg) || 0;
  const caseMaterialKg = Number(linkedPresentation?.masterCaseWeightKg) || 0;
  const suggestedCaseWeightKg = (unitNetKg != null && unitsPerCase > 0)
    ? (unitNetKg + wrapperKg) * unitsPerCase + retailBoxKg * packsPerCase + caseMaterialKg
    : null;
  const suggestedCaseWeight = suggestedCaseWeightKg != null ? fromKgWeight(suggestedCaseWeightKg, weightUnit) : null;
  const caseWeightBreakdown = suggestedCaseWeightKg != null
    ? `(${fromKgWeight(unitNetKg, weightUnit).toFixed(3)} unit net + ${fromKgWeight(wrapperKg, weightUnit).toFixed(3)} wrapper) × ${unitsPerPack} units/pack × ${packsPerCase} packs/case (${unitsPerCase} units total)`
      + (retailBoxKg > 0 ? ` + ${fromKgWeight(retailBoxKg, weightUnit).toFixed(3)} retail box × ${packsPerCase} packs` : '')
      + (caseMaterialKg > 0 ? ` + ${fromKgWeight(caseMaterialKg, weightUnit).toFixed(2)} case material` : '')
      + ` = ${suggestedCaseWeight.toFixed(2)} ${weightUnit}`
    : null;

  // ── Net / Gross weight for the full pallet load ──
  const totalUnitsOnPallet = derived.totalCases * unitsPerCase;
  const totalNetKg = (unitNetKg != null && totalUnitsOnPallet > 0) ? unitNetKg * totalUnitsOnPallet : null;
  const totalNetWeight = totalNetKg != null ? fromKgWeight(totalNetKg, weightUnit) : null;
  const caseTotalKg = derived.totalWeight ? toKgWeight(Number(derived.totalWeight), weightUnit) : null;
  const totalGrossKg = caseTotalKg != null ? caseTotalKg + (companyPalletWeightKg || 0) : null;
  const totalGrossWeight = totalGrossKg != null ? fromKgWeight(totalGrossKg, weightUnit) : null;
  const palletWeightDisplay = companyPalletWeightKg ? fromKgWeight(companyPalletWeightKg, weightUnit) : 0;

  return (
    <div className="mb-3 p-2 rounded" style={{ border: '1px solid #dee2e6', background: '#fcfcfd' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold" style={{ fontSize: 12 }}>Presentation {index + 1}</span>
        <button type="button" className="btn btn-outline-danger btn-sm" style={{ fontSize: 11, padding: '2px 8px' }} onClick={onRemove}>
          Remove
        </button>
      </div>

      <div className="row g-2 mb-2">
        <div className="col-6">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Link to product presentation</label>
          <select
            className="form-select form-select-sm"
            value={block.presentationId || ''}
            onChange={e => onLink(e.target.value)}
          >
            <option value="">— none / custom —</option>
            {productPresentations.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="col-6">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Label</label>
          <input
            type="text" className="form-control form-control-sm"
            placeholder="e.g. Retail Single, Food Service"
            value={block.label} onChange={e => onField('label', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Unit Sale</label>
          <input
            type="text" className="form-control form-control-sm"
            placeholder="1 pizza"
            value={block.unitSale} onChange={e => onField('unitSale', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Units per Master Case</label>
          <input
            type="number" className="form-control form-control-sm"
            placeholder="30"
            value={block.unitsPerMasterCase} onChange={e => onField('unitsPerMasterCase', e.target.value)}
          />
        </div>
        <div className="col-12">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Packaging Type</label>
          <input
            type="text" className="form-control form-control-sm"
            placeholder="Individually wrapped and boxed"
            value={block.packagingType} onChange={e => onField('packagingType', e.target.value)}
          />
        </div>
        <div className="col-6">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>
            Case UPC {block.presentationId && <span className="text-muted" title="Auto-filled from the linked product presentation — edit to override">(auto)</span>}
          </label>
          <input
            type="text" className="form-control form-control-sm font-monospace"
            placeholder="14-digit UPC/GTIN" maxLength={14}
            value={block.caseUpc} onChange={e => onField('caseUpc', e.target.value.replace(/\D/g, ''))}
          />
        </div>
        <div className="col-6">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>
            Unit UPC {block.presentationId && <span className="text-muted" title="Auto-filled from the linked product presentation — edit to override">(auto)</span>}
          </label>
          <input
            type="text" className="form-control form-control-sm font-monospace"
            placeholder="12-digit UPC-A" maxLength={12}
            value={block.unitUpc} onChange={e => onField('unitUpc', e.target.value.replace(/\D/g, ''))}
          />
        </div>
      </div>

      {/* Master case dimensions — feeds pallet unit dims */}
      <div className="mb-2">
        <label className="form-label mb-1" style={{ fontSize: 11, fontWeight: 600 }}>
          Master Case Dimensions (L × W × H)
        </label>
        <div className="d-flex gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ maxWidth: 110 }}>
            <input type="number" className="form-control form-control-sm" placeholder="L" {...pf('unitDimL')} />
            <span className="input-group-text" style={{ fontSize: 11 }}>{dimUnit}</span>
          </div>
          <span style={{ fontSize: 13, color: '#999' }}>×</span>
          <div className="input-group input-group-sm" style={{ maxWidth: 110 }}>
            <input type="number" className="form-control form-control-sm" placeholder="W" {...pf('unitDimW')} />
            <span className="input-group-text" style={{ fontSize: 11 }}>{dimUnit}</span>
          </div>
          <span style={{ fontSize: 13, color: '#999' }}>×</span>
          <div className="input-group input-group-sm" style={{ maxWidth: 110 }}>
            <input type="number" className="form-control form-control-sm" placeholder="H" {...pf('unitDimH')} />
            <span className="input-group-text" style={{ fontSize: 11 }}>{dimUnit}</span>
          </div>
          <select className="form-select form-select-sm" style={{ maxWidth: 65 }} {...pf('dimUnit')}>
            <option value="in">in</option>
            <option value="cm">cm</option>
          </select>
        </div>
      </div>

      {/* Pallet load */}
      <div className="row g-2 mb-2">
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Pallet Length</label>
          <div className="input-group input-group-sm">
            <input type="number" className="form-control form-control-sm" placeholder="48" {...pf('palletL')} />
            <span className="input-group-text" style={{ fontSize: 11 }}>{dimUnit}</span>
          </div>
        </div>
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Pallet Width</label>
          <div className="input-group input-group-sm">
            <input type="number" className="form-control form-control-sm" placeholder="40" {...pf('palletW')} />
            <span className="input-group-text" style={{ fontSize: 11 }}>{dimUnit}</span>
          </div>
        </div>
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Pallet Height</label>
          <div className="input-group input-group-sm">
            <input type="number" className="form-control form-control-sm" placeholder="6" {...pf('palletH')} />
            <span className="input-group-text" style={{ fontSize: 11 }}>{dimUnit}</span>
          </div>
        </div>
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Units per Layer</label>
          <input type="number" className="form-control form-control-sm" placeholder="6" {...pf('unitsPerLayer')} />
        </div>
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>
            Columns per Layer
            <span className="text-muted ms-1" style={{ fontSize: 10 }} title="Boxes across the pallet length direction">(?)</span>
          </label>
          <input type="number" className="form-control form-control-sm" placeholder="3" {...pf('gridCols')} />
        </div>
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>Number of Layers</label>
          <input type="number" className="form-control form-control-sm" placeholder="6" {...pf('numberOfLayers')} />
        </div>
        <div className="col-4">
          <label className="form-label mb-0" style={{ fontSize: 11 }}>
            Case Weight
            {caseWeightBreakdown && (
              <span className="text-muted ms-1" style={{ fontSize: 10 }} title={caseWeightBreakdown}>(?)</span>
            )}
          </label>
          <div className="input-group input-group-sm">
            <input type="number" className="form-control form-control-sm" placeholder="e.g. 3.5" {...pf('unitWeightValue')} />
            <select
              className="form-select form-select-sm"
              style={{ flex: '0 0 72px', width: 72 }}
              {...pf('weightUnit')}
            >
              <option value="lbs">lbs</option>
              <option value="kg">kg</option>
            </select>
          </div>
          {suggestedCaseWeight != null && (
            <button
              type="button"
              className="btn btn-link p-0 mt-1"
              style={{ fontSize: 10 }}
              title={caseWeightBreakdown}
              onClick={() => onPalletField('unitWeightValue', suggestedCaseWeight.toFixed(2))}
            >
              Use suggested: {suggestedCaseWeight.toFixed(2)} {weightUnit}
            </button>
          )}
        </div>
      </div>

      <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6, padding: 10, marginBottom: 8 }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>TOP-DOWN ARRANGEMENT</span>
          {suggestion && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              style={{ fontSize: 10, padding: '2px 8px' }}
              onClick={() => {
                onPalletField('gridCols', String(suggestion.cols));
                onPalletField('unitsPerLayer', String(suggestion.units));
                onPalletField('mixedZone', suggestion.mixedZone || null);
              }}
            >
              Auto-fit: {suggestion.cols} cols × {suggestion.rows} rows = {suggestion.mainUnits}/layer
              {suggestion.mixedZone && ` + ${suggestion.mixedZone.units} rotated = ${suggestion.units}/layer`}
            </button>
          )}
        </div>
        <PalletDiagram config={pallet} mode="top" />
        <div style={{ fontSize: 11, color: '#666', marginTop: 4, textAlign: 'center' }}>
          {derived.rows > 0 && (
            <>
              {Number(pallet.gridCols) || '?'} cols × {derived.rows} rows = {Number(pallet.unitsPerLayer) || '?'} per layer
              {derived.nonUniform && (
                <span className="ms-1 badge text-bg-secondary" style={{ fontSize: 10 }}>
                  last row: {derived.lastRowCount} boxes
                </span>
              )}
              {derived.mixedZone && (
                <span className="ms-1 badge text-bg-info" style={{ fontSize: 10 }}>
                  +{derived.mixedZone.units} rotated ({derived.mixedZone.cols}×{derived.mixedZone.rows})
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-2">
        {derived.totalCases > 0 && (
          <span className="badge text-bg-primary" style={{ fontSize: 11 }}>
            Total cases: <strong>{derived.totalCases}</strong>
          </span>
        )}
        {derived.totalWeight && (
          <span className="badge text-bg-light border" style={{ fontSize: 11 }}>
            Total weight: <strong>{derived.totalWeight} {pallet.weightUnit || 'lbs'}</strong>
          </span>
        )}
        {derived.surfaceUsage && (
          <span className="badge text-bg-light border" style={{ fontSize: 11 }}>
            Surface usage: <strong>{derived.surfaceUsage}%</strong>
          </span>
        )}
        {Number(derived.overhangL) > 0 && (
          <span className="badge text-bg-warning" style={{ fontSize: 11 }}>
            Overhang L: {derived.overhangL}{dimUnit} per side
          </span>
        )}
        {Number(derived.overhangW) > 0 && (
          <span className="badge text-bg-warning" style={{ fontSize: 11 }}>
            Overhang W: {derived.overhangW}{dimUnit} per side
          </span>
        )}
      </div>

      {(totalNetWeight != null || totalGrossWeight != null) && (
        <div className="d-flex flex-wrap gap-3 mb-1" style={{ fontSize: 12 }}>
          {totalNetWeight != null && (
            <div>
              Total Net Weight: <strong>{totalNetWeight.toFixed(1)} {weightUnit}</strong>
            </div>
          )}
          {totalGrossWeight != null && (
            <div>
              Total Gross Weight: <strong>{totalGrossWeight.toFixed(1)} {weightUnit}</strong>
              <div style={{ fontSize: 9, color: '#999' }}>
                Includes pallet weight ({palletWeightDisplay.toFixed(1)} {weightUnit})
              </div>
            </div>
          )}
        </div>
      )}
      {(totalNetWeight != null || totalGrossWeight != null) && (
        <div className="mb-2" style={{ fontSize: 10, color: '#999' }}>
          Computed, not directly editable — if these look wrong, fix the source: product net/wrapper
          weight (Product Info tab), units per pack, retail box weight &amp; master case weight
          (Packaging tab), Case Weight/packs-per-case above, or company pallet weight (Company Settings).
        </div>
      )}

      <ImageUploadRow
        label="Retail Packaging Photo"
        value={block.retailPackagingImageUrl}
        onChange={url => onField('retailPackagingImageUrl', url)}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <div className="fw-semibold mb-2 pb-1" style={{ fontSize: 12, color: '#555', borderBottom: '1px solid #e9ecef', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="mb-2">
      <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
        {label}
        {hint && <span className="fw-normal text-muted ms-1" style={{ fontSize: 11 }}>— {hint}</span>}
      </label>
      {children}
    </div>
  );
}
